import m from "mithril";
import { getMithril } from "../utils.js";

const LoanMining = {
    customers: [],
    isLoading: true,
    // Mining parameters with improved defaults
    params: {
        incomeThresholds: '2000000,5000000,8000000',
        ageThresholds: '25,35,45',
        minConfidence: 0.6,
        maxDepth: 8,
        attributes: 'status_pernikahan,status_rumah,penghasilan,umur',
        enableOverrides: true
    },

    oninit: function () {
        this.loadCustomers();
    },

    loadCustomers: function () {
        this.loadCustomersWithParams(this.params);
    },

    loadCustomersWithParams: function (params) {
        // Build query string with parameters
        const queryParams = new URLSearchParams({
            incomeThresholds: params.incomeThresholds,
            ageThresholds: params.ageThresholds,
            minConfidence: params.minConfidence,
            maxDepth: params.maxDepth,
            attributes: params.attributes,
            enableOverrides: params.enableOverrides
        });

        // Fetch customers via API call with parameters (cookie-based auth via getMithril)
        getMithril(`/api/customers-mining?${queryParams.toString()}`)
            .then(result => {
                if (result.success) {
                    this.customers = result.customers;
                    this.isLoading = false;
                } else {
                    console.error('API returned error:', result.error);
                    this.isLoading = false;
                }
                m.redraw();
            })
            .catch(error => {
                console.error('Error loading customers:', error);
                this.isLoading = false;
                m.redraw();
            });
    },

    processMining: function () {
        this.isLoading = true;
        m.redraw();
        this.loadCustomersWithParams(this.params);
    },

    calculatePredictedClass: function (customer) {
        // Use the same prediction logic from LoanPrediction component
        let score = 50; // Base score

        // Marital status factor
        if (customer.status_kawin === 'Kawin') {
            score += 15;
        } else if (customer.status_kawin === 'Belum Kawin') {
            score += 10;
        }

        // Housing status factor
        if (customer.status_rumah === 'Milik Sendiri') {
            score += 20;
        } else if (customer.status_rumah === 'Sewa') {
            score += 5;
        }

        // Income factor (simplified)
        const income = customer.penghasilan ? parseInt(customer.penghasilan.toString().replace(/[^\d]/g, '')) : 0;
        if (income >= 8000000) {
            score += 25;
        } else if (income >= 5000000) {
            score += 15;
        } else if (income >= 3000000) {
            score += 10;
        } else if (income >= 2000000) {
            score += 5;
        }

        // Age factor
        const age = customer.tgl_lahir ? new Date().getFullYear() - new Date(customer.tgl_lahir).getFullYear() : 30;
        if (age >= 25 && age <= 55) {
            score += 15;
        } else if (age >= 18 && age <= 65) {
            score += 10;
        }

        // Return class based on score
        if (score >= 80) return 'Sangat Baik';
        if (score >= 60) return 'Baik';
        if (score >= 40) return 'Sedang';
        return 'Berisiko';
    },

    formatIncome: function (amount) {
        if (!amount) return '0';
        const numericAmount = amount.toString().replace(/[^\d]/g, '');
        return new Intl.NumberFormat('id-ID').format(numericAmount);
    },

    view: function () {
        return m("div", { class: "max-w-7xl mx-auto p-6" }, [
            m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow-md" }, [
                m("div", { class: "p-6 border-b border-gray-200 dark:border-gray-700" }, [
                    m("h1", { class: "text-2xl font-bold text-gray-800 dark:text-white flex items-center" }, [
                        m("i", { class: "ri-database-line mr-3 text-orange-500" }),
                        "Proses Mining Data Nasabah"
                    ]),
                    m("p", { class: "mt-2 text-gray-600 dark:text-gray-400" },
                        "Analisis data nasabah untuk prediksi kelancaran pinjaman menggunakan algoritma klasifikasi"
                    )
                ]),

                m("div", { class: "p-6" }, [
                    // Parameters Section
                    m("div", { class: "bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6" }, [
                        m("h2", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-4" }, "Parameter Mining"),
                        m("div", { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" }, [
                            m("div", [
                                m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Threshold Penghasilan (Rp)"),
                                m("input", {
                                    type: "text",
                                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white",
                                    value: this.params.incomeThresholds,
                                    oninput: (e) => this.params.incomeThresholds = e.target.value
                                }),
                                m("p", { class: "text-xs text-gray-500 mt-1" }, "Format: 1000000,2000000,3000000")
                            ]),
                            m("div", [
                                m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Threshold Umur"),
                                m("input", {
                                    type: "text",
                                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white",
                                    value: this.params.ageThresholds,
                                    oninput: (e) => this.params.ageThresholds = e.target.value
                                }),
                                m("p", { class: "text-xs text-gray-500 mt-1" }, "Format: 30,40,50")
                            ]),
                            m("div", [
                                m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Minimum Confidence"),
                                m("input", {
                                    type: "number",
                                    step: "0.1",
                                    min: "0",
                                    max: "1",
                                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white",
                                    value: this.params.minConfidence,
                                    oninput: (e) => this.params.minConfidence = parseFloat(e.target.value) || 0.0
                                }),
                                m("p", { class: "text-xs text-gray-500 mt-1" }, "Range: 0.0 - 1.0")
                            ]),
                            m("div", [
                                m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Maximum Depth"),
                                m("input", {
                                    type: "number",
                                    min: "1",
                                    max: "20",
                                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white",
                                    value: this.params.maxDepth,
                                    oninput: (e) => this.params.maxDepth = parseInt(e.target.value) || 10
                                }),
                                m("p", { class: "text-xs text-gray-500 mt-1" }, "Range: 1 - 20")
                            ]),
                            m("div", [
                                m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Atribut yang Digunakan"),
                                m("input", {
                                    type: "text",
                                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white",
                                    value: this.params.attributes,
                                    oninput: (e) => this.params.attributes = e.target.value
                                }),
                                m("p", { class: "text-xs text-gray-500 mt-1" }, "Format: atribut1,atribut2,atribut3")
                            ]),
                            m("div", { class: "flex items-center" }, [
                                m("input", {
                                    type: "checkbox",
                                    id: "enableOverrides",
                                    class: "h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded",
                                    checked: this.params.enableOverrides,
                                    onchange: (e) => this.params.enableOverrides = e.target.checked
                                }),
                                m("label", { for: "enableOverrides", class: "ml-2 block text-sm text-gray-700 dark:text-gray-300" }, [
                                    "Aktifkan Business Rules Override",
                                    m("p", { class: "text-xs text-gray-500 mt-1" }, "Override otomatis untuk nasabah high-income")
                                ])
                            ])
                        ]),
                        m("div", { class: "mt-4 flex justify-end" }, [
                            m("button", {
                                class: "bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50",
                                onclick: () => this.processMining(),
                                disabled: this.isLoading
                            }, [
                                this.isLoading ? m("i", { class: "ri-loader-4-line animate-spin mr-2" }) : m("i", { class: "ri-play-line mr-2" }),
                                "Proses Mining"
                            ])
                        ])
                    ]),

                    this.isLoading ? m("div", { class: "text-center py-8" }, [
                        m("i", { class: "ri-loader-4-line animate-spin text-2xl text-orange-500" }),
                        m("p", { class: "mt-2 text-gray-600 dark:text-gray-400" }, "Memuat data nasabah...")
                    ]) : m("div", { class: "overflow-x-auto" }, [
                        m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                            m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                                m("tr", [
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "No"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nama"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Status Pernikahan"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Status Rumah"),
                                    m("th", { class: "px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Penghasilan"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Umur"),
                                    m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Kelas")
                                ])
                            ]),
                            m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                                this.customers.length === 0 ? m("tr", [
                                    m("td", { class: "px-6 py-4 text-center text-gray-500 dark:text-gray-400", colspan: "7" }, "Tidak ada data nasabah")
                                ]) : this.customers.map((customer, index) => {
                                    return m("tr", { class: "hover:bg-gray-50 dark:hover:bg-gray-700" }, [
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" }, index + 1),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, customer.nama || '-'),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, customer.status_pernikahan || '-'),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, customer.status_rumah || '-'),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right" }, `Rp ${this.formatIncome(customer.penghasilan)}`),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" }, customer.umur || '-'),
                                        m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                            m("span", {
                                                class: `inline-flex px-2 py-1 text-xs font-semibold rounded-full ${customer.kelas === 'lancar' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                                        customer.kelas === 'macet' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                            customer.kelas === 'Sangat Baik' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                                                customer.kelas === 'Baik' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                                                    customer.kelas === 'Sedang' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                                                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                                    }`
                                            }, customer.kelas || '-')
                                        ])
                                    ]);
                                })
                            ])
                        ]),

                        // Summary statistics
                        this.customers.length > 0 && m("div", { class: "mt-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4" }, [
                            m("h3", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-4" }, "Ringkasan Klasifikasi"),
                            m("div", { class: "grid grid-cols-2 md:grid-cols-4 gap-4" }, [
                                m("div", { class: "bg-green-100 dark:bg-green-900 p-3 rounded-lg" }, [
                                    m("div", { class: "text-2xl font-bold text-green-800 dark:text-green-200" },
                                        this.customers.filter(c => c.kelas === 'lancar').length
                                    ),
                                    m("div", { class: "text-sm text-green-600 dark:text-green-400" }, "Lancar")
                                ]),
                                m("div", { class: "bg-red-100 dark:bg-red-900 p-3 rounded-lg" }, [
                                    m("div", { class: "text-2xl font-bold text-red-800 dark:text-red-200" },
                                        this.customers.filter(c => c.kelas === 'macet').length
                                    ),
                                    m("div", { class: "text-sm text-red-600 dark:text-red-400" }, "Macet")
                                ]),
                                m("div", { class: "bg-blue-100 dark:bg-blue-900 p-3 rounded-lg" }, [
                                    m("div", { class: "text-2xl font-bold text-blue-800 dark:text-blue-200" },
                                        this.customers.filter(c => c.kelas === 'Sangat Baik' || c.kelas === 'Baik').length
                                    ),
                                    m("div", { class: "text-sm text-blue-600 dark:text-blue-400" }, "Baik")
                                ]),
                                m("div", { class: "bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg" }, [
                                    m("div", { class: "text-2xl font-bold text-yellow-800 dark:text-yellow-200" },
                                        this.customers.filter(c => c.kelas === 'Sedang' || c.kelas === 'Berisiko').length
                                    ),
                                    m("div", { class: "text-sm text-yellow-600 dark:text-yellow-400" }, "Risiko")
                                ])
                            ])
                        ])
                    ])
                ])
            ])
        ]);
    }
};

export default LoanMining;